/* EB FAQ accordion — height-animated open/close for <details> cards.

   Native <details> snaps open with no transition, so the summary click is taken
   over and the answer box is animated with the Web Animations API (which cancels
   cleanly when a shopper clicks faster than the animation, unlike a transition +
   transitionend pair). Without JS the accordion still works — it is just instant,
   which is the point of building on <details> rather than divs and ARIA. */

if (!customElements.get('eb-faq')) {
  customElements.define(
    'eb-faq',
    class EbFaq extends HTMLElement {
      connectedCallback() {
        this.allowMultiple = this.dataset.allowMultiple === 'true';
        this.onSummaryClick = this.handleSummaryClick.bind(this);
        this.addEventListener('click', this.onSummaryClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onSummaryClick);
      }

      get items() {
        return Array.from(this.querySelectorAll('.eb-faq__item'));
      }

      get reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      handleSummaryClick(event) {
        const summary = event.target.closest('.eb-faq__q');
        if (!summary || !this.contains(summary)) return;

        // let a link inside the question behave like a link
        if (event.target.closest('a')) return;

        const item = summary.closest('.eb-faq__item');
        if (!item) return;

        // drive open/close ourselves so the change can be animated
        event.preventDefault();

        if (item.open) {
          this.collapse(item);
        } else {
          this.expand(item);
        }
      }

      expand(item) {
        if (!this.allowMultiple) {
          this.items.forEach((other) => {
            if (other !== item && other.open) this.collapse(other);
          });
        }

        const panel = item.querySelector('.eb-faq__a');
        if (!panel) {
          item.open = true;
          return;
        }

        // Start from whatever height is on screen right now, not a hard 0 — an
        // interrupted close is mid-collapse, and restarting from 0 would jump.
        // Measure before `slide` cancels the running animation.
        const from = panel.getBoundingClientRect().height;
        item.classList.remove('is-closing');
        // a closed <details> hides the panel, so open it before measuring content
        item.open = true;

        /* Chrome wraps <details> content in `::details-content`, which is
           `content-visibility: hidden` while closed — so straight after flipping
           `open` the panel can still measure 0. Animating 0 → 0 is what made
           opening look instant while closing stayed smooth (closing measures a
           panel that is already open). If the content has not been laid out yet,
           wait one frame and measure again. */
        const run = () => this.slide(panel, from, panel.scrollHeight);
        if (panel.scrollHeight > 0) {
          run();
        } else {
          requestAnimationFrame(run);
        }
      }

      collapse(item) {
        const panel = item.querySelector('.eb-faq__a');
        if (!panel) {
          item.open = false;
          return;
        }

        // measure while it is still open, and keep it open until the end
        const from = panel.getBoundingClientRect().height;
        // `open` has to stay set for the animation, so flip the +/− glyph with a
        // class instead — otherwise it stays a minus until the panel finishes
        item.classList.add('is-closing');

        this.slide(panel, from, 0, () => {
          item.open = false;
          item.classList.remove('is-closing');
        });
      }

      slide(panel, from, to, onDone) {
        panel.ebAnimation?.cancel();

        if (this.reduceMotion || typeof panel.animate !== 'function') {
          panel.style.height = '';
          onDone?.();
          return;
        }

        const animation = panel.animate(
          [{ height: `${from}px` }, { height: `${to}px` }],
          { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
        );
        panel.ebAnimation = animation;

        animation.onfinish = () => {
          panel.ebAnimation = null;
          // hand the height back to layout so the answer can reflow on resize
          panel.style.height = '';
          onDone?.();
        };
      }
    }
  );
}
